import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import Loader from "components/Loader";
import MonthFilter from "components/MonthFilter";
import { usageApi, UsageData } from "api/usage";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import SubscriptionInfoCard from "./components/SubscriptionInfoCard";
import { useTranslation } from "react-i18next";

const UsageOverview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Month filter state
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    return dayjs().format("YYYY-MM");
  });

  const handleMonthChange = useCallback((month: string) => {
    setSelectedMonth(month);
  }, []);

  // Calculate start and end dates from selected month
  const getDateRangeFromMonth = useCallback((month: string) => {
    const startDate = dayjs(month).startOf("month").format("YYYY-MM-DD");
    const endDate = dayjs(month).endOf("month").format("YYYY-MM-DD");
    return { startDate, endDate };
  }, []);

  const fetchUsageData = useCallback(async () => {
    // Only fetch if month is valid
    if (!selectedMonth) {
      return;
    }

    try {
      setIsLoading(true);

      const { startDate, endDate } = getDateRangeFromMonth(selectedMonth);

      const data = await usageApi.getUsageOverview({
        start_date: startDate,
        end_date: endDate,
      });

      setUsageData(data);
    } catch (err: any) {
      console.error("Error fetching usage data:", err);
      toast.error(
        err.response?.data?.message || t("usageOverview.errors.fetchUsageData")
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonth, getDateRangeFromMonth, t]);

  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  const safeNumber = (value: number | null | undefined): number => {
    return value && !isNaN(value) ? value : 0;
  };

  const formatStorageSize = (sizeInMB: number | null | undefined) => {
    const size = safeNumber(sizeInMB);
    if (size < 1024) {
      return `${size.toFixed(2)} MB`;
    } else {
      return `${(size / 1024).toFixed(1)} GB`;
    }
  };

  const calculatePercentage = (
    current: number | null | undefined,
    max: number | null | undefined
  ) => {
    const currentValue = safeNumber(current);
    const maxValue = safeNumber(max);
    if (maxValue === 0) return 100; // Return 100% when max is 0 (0 used out of 0 available = 100% utilized)
    return Math.min((currentValue / maxValue) * 100, 100);
  };

  if (isLoading) {
    return <Loader txt={t("usageOverview.loadingUsageData")} />;
  }

  if (!usageData) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-gray-600">{t("usageOverview.noUsageData")}</span>
      </div>
    );
  }

  const documentUsagePercent = calculatePercentage(
    usageData.total_document_storage_size_mb,
    usageData.max_storage_size_mb
  );

  return (
    <div className="mt-3 space-y-6">
      {/* Subscription Info Card */}
      {usageData.subscriptionInfo && (
        <SubscriptionInfoCard subscriptionInfo={usageData.subscriptionInfo} />
      )}

      {/* Usage Overview Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Users */}
        <Card extra="p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">
            {t("usageOverview.cards.users")}
          </h3>
          <div className="text-3xl font-bold text-purple-600">
            {safeNumber(usageData.total_users)}/
            {safeNumber(usageData.max_users)}
          </div>
        </Card>

        {/* Workspaces */}
        <Card extra="p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">
            {t("usageOverview.cards.workspaces")}
          </h3>
          <div className="text-3xl font-bold text-blue-600">
            {safeNumber(usageData.total_workspaces)}/
            {safeNumber(usageData.max_workspaces)}
          </div>
        </Card>

        {/* Agents */}
        <Card extra="p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">
            {t("usageOverview.cards.agents")}
          </h3>
          <div className="text-3xl font-bold text-green-600">
            {safeNumber(usageData.total_agents)}/
            {safeNumber(usageData.max_agents)}
          </div>
        </Card>

        {/* Workflows */}
        <Card extra="p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">
            {t("usageOverview.cards.workflows")}
          </h3>
          <div className="text-3xl font-bold text-orange-600">
            {safeNumber(usageData.total_workflows)}/
            {safeNumber(usageData.max_workflows)}
          </div>
        </Card>
      </div>

      {/* Storage Usage */}
      <Card extra="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
            {t("usageOverview.documentStorage.title")}
          </h3>
          <p className="text-sm text-gray-600">
            {t("usageOverview.documentStorage.usage", {
              used: formatStorageSize(usageData.total_document_storage_size_mb),
              total: formatStorageSize(usageData.max_storage_size_mb),
            })}
          </p>
        </div>
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className="h-2.5 rounded-full bg-red-600 transition-all duration-300"
            style={{
              width: `${documentUsagePercent}%`,
            }}
          ></div>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          {t("usageOverview.documentStorage.percentUsed", {
            percent: documentUsagePercent.toFixed(1),
          })}
        </div>
      </Card>

      {/* Token Usage Items */}
      <>
        {/* Month Filter */}
        <MonthFilter
          selectedMonth={selectedMonth}
          onMonthChange={handleMonthChange}
          isLoading={isLoading}
          title={t("usageOverview.monthFilterTitle")}
          description={t("usageOverview.monthFilterDescription")}
        />

        <Card extra="p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
              {t("usageOverview.tokenUsage.title")}
            </h3>
            <p className="text-sm text-gray-600">
              {t("usageOverview.tokenUsage.subtitle")}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
              <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    {t("usageOverview.tokenUsage.headers.apiKey")}
                  </th>
                  <th scope="col" className="px-6 py-3">
                    {t("usageOverview.tokenUsage.headers.totalTokens")}
                  </th>
                  <th scope="col" className="px-6 py-3">
                    {t("usageOverview.tokenUsage.headers.maxTokens")}
                  </th>
                  <th scope="col" className="px-6 py-3">
                    {t("usageOverview.tokenUsage.headers.action")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {usageData.token_usage_items &&
                usageData.token_usage_items.length > 0 ? (
                  usageData.token_usage_items.map((item, index) => (
                    <tr
                      key={index}
                      className="border-b bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-600"
                    >
                      <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                        {item.api_key_id?.substring(0, 16)}...
                      </td>
                      <td className="px-6 py-4">
                        {safeNumber(item.total_tokens).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        {safeNumber(item.max_tokens).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => {
                            navigate(
                              `/dashboard/usage-overview/history/${item.api_key_id}`
                            );
                          }}
                          className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {t("usageOverview.tokenUsage.viewHistory")}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      {t("usageOverview.tokenUsage.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </>
    </div>
  );
};

export default UsageOverview;
